import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../../firebase/AuthProvider";
import {
    GetAllHolidaysAsync,
    DeleteHolidayAsync
} from "../../../services/HolidaysService/HolidaysService";
import { GetHolidaysQueryReq, HolidayResponse } from "../../../interfaces";
import { AxiosError } from "axios";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import AddHolidayModal from "./Modals/AddHolidayModal";
import EditHolidayModal from "./Modals/EditHolidayModal";
import moment from "moment";
import { PaginationDto } from "../../../interfaces/Employee/Employee";
import { PAGINATION } from "../../../Data/SD";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";

type AddHolidayFormProps = {
    onSuccess?: () => void;
};

const AllHolidays: React.FC<AddHolidayFormProps> = ({ onSuccess }) => {
    const { currentUser } = useAuth();

    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: PAGINATION.PageNumber, pageSize: PAGINATION.PageSize });

    const [loading, setLoading] = useState<{ getHoliday: boolean }>({
        getHoliday: false,
    });

    const Edit = FiEdit2 as any;
    const Delete = RiDeleteBinLine as any;

    const [totalCount, setTotalCount] = useState<number>(0);
    const [holidays, setHolidays] = useState<HolidayResponse[]>([]);

    const [sort, setSort] = useState<{
        column: string;
        order: "asc" | "desc";
    }>({
        column: "createddate", // title, startdate, enddate, createddate, updateddate
        order: "desc",
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [holidayToEdit, setHolidayToEdit] = useState<HolidayResponse | null>(null);

    useEffect(() => {
        GetAllHolidays();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, pagination]);

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return "--";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "--";
        return d.toLocaleString();
    };

    const GetAllHolidays = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;

        const payload: GetHolidaysQueryReq = {
            pageNumber: 1,
            pageSize: 10,
            sortBy: sort.column || null,
            sortDirection: sort.order || null,
            from: null,
            to: null,
        };

        try {
            setLoading((prev) => ({ ...prev, getHoliday: true }));
            const res = await GetAllHolidaysAsync(token, payload);
            if (res.success) {
                setTotalCount(res.data.totalCount);
                setHolidays(res.data.items || []);
            } else {
                toast.error(res.message);
            }

        } catch (error: any) {
            toast.error(
                error instanceof AxiosError
                    ? error.response?.data.message
                    : error.message || "Error To Get Holidays"
            );
        } finally {
            setLoading((prev) => ({ ...prev, getHoliday: false }));
        }
    };

    function handleSort(column: string) {
        setSort((prev) => {
            if (prev.column === column) {
                return { column, order: prev.order === "asc" ? "desc" : "asc" };
            }
            return { column, order: "desc" };
        });
    }

    function getSortIcon(column: string) {
        if (sort.column !== column) return "⇅";
        return sort.order === "asc" ? "↑" : "↓";
    }

    const onEdit = (id: number) => {
        const h = holidays.find(x => x.id === id);
        if (h) {
            setHolidayToEdit(h);
            setEditModalOpen(true);
        }
    };

    const onDelete = async (id: number) => {
        const confirmed = await showConfirmationDialog(
            "Confirm delete",
            "Are you sure you want to delete this holiday?",
            "Delete",
            "Cancel",
            "warning"
        );
        if (!confirmed) return;

        const token = await currentUser?.getIdToken();
        if (!token) return;

        try {
            setLoading((prev) => ({ ...prev, getHoliday: true }));
            const res = await DeleteHolidayAsync(token, id);
            if (res.success) {
                toast.success(res.message || "Holiday deleted successfully");
                GetAllHolidays();
                onSuccess?.();
            } else {
                toast.error(res.message || "Failed to delete holiday");
            }
        } catch (err: any) {
            toast.error(err instanceof AxiosError ? err.response?.data.message : err?.message || "Error deleting holiday");
        } finally {
            setLoading((prev) => ({ ...prev, getHoliday: false }));
        }
    };

    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    return (
        <div className="">
            {/* Header */}
            {/* <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Holidays</h2>

                <Button
                    variant="primary"
                    className="btn_sm"
                    onClick={() => setModalOpen(true)}
                >
                    Add New Holiday
                </Button>
            </div> */}

            <div className="bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#FBFBFC] border-b">
                        <tr>
                            <th className="ps-6 py-3 cursor-pointer font-medium text-[#0A0A0A]" onClick={() => handleSort("title")}>
                                Holiday Name {getSortIcon("title")}
                            </th>

                            <th className="ps-8 py-3 cursor-pointer font-medium text-[#0A0A0A]" onClick={() => handleSort("startdate")}>
                                Date {getSortIcon("startdate")}
                            </th>

                            <th className="py-3  text-center font-medium text-[#0A0A0A]">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading.getHoliday && (
                            <tr>
                                <td colSpan={7} className="px-4 py-6 text-center">
                                    <Spinner animation="border" size="sm" /> Loading...
                                </td>
                            </tr>
                        )}

                        {!loading.getHoliday && holidays.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                                    No holidays found.
                                </td>
                            </tr>
                        )}

                        {!loading.getHoliday &&
                            holidays.map((h) => (
                                <tr
                                    key={h.id}
                                    className="border-b hover:bg-gray-50 transition"
                                >
                                    <td
                                        className="ps-4 py-3 font-medium text-[#0A0A0A] break-all overflow-hidden"
                                        style={{ width: "150px", maxWidth: "150px" }}
                                    >
                                        {h.title}
                                    </td>

                                    {/* Scrollable Description */}
                                    {/* <td className="px-4 py-3">
                                        <div className="max-h-28 overflow-y-auto pr-2 text-gray-700 leading-relaxed">
                                            {h.description}
                                        </div>
                                    </td> */}

                                    <td className="px-4 py-3 whitespace-nowrap text-[#717182]">
                                        {moment(h.startDate).isSame(h.endDate, "day")
                                            ? moment(h.startDate).format("MM-DD")
                                            : `${moment(h.startDate).format("MM-DD")} to ${moment(h.endDate).format("MM-DD")}`}
                                    </td>

                                    {/* <td className="px-4 py-3 whitespace-nowrap">
                                        {formatDate(h.createdAt)}
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {formatDate(h.updatedAt)}
                                    </td> */}

                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => onEdit(h.id)}
                                            className="text-[#717182] hover:text-blue-500 mr-3"
                                            title="Edit"
                                        >
                                            <Edit size={18} />
                                        </button>

                                        <button
                                            onClick={() => onDelete(h.id)}
                                            className="text-[#717182] hover:text-red-500"
                                            title="Delete"
                                        >
                                            <Delete size={19} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {/* {totalCount > 0 && (
                <Pagination
                    currentPage={pagination.pageNumber}
                    totalPages={Math.ceil(totalCount / pagination.pageSize)}
                    onPageChange={onPageChange}
                    pageSize={pagination.pageSize}
                    resetPageSize={resetPageSize}
                />
            )} */}

            {/* Modals */}
            {modalOpen && (
                <AddHolidayModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSuccess={GetAllHolidays}
                />
            )}

            {editModalOpen && (
                <EditHolidayModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    holiday={holidayToEdit}
                    onSuccess={() => {
                        setEditModalOpen(false);
                        GetAllHolidays();
                        onSuccess?.();
                    }}
                />
            )}
        </div>
    );
};

export default AllHolidays;